/**
 * CompanyDashboard — super-admin only.
 *
 * Manage the companies on ElecBid: add a company, toggle which tabs/features
 * each one gets, and see their setup. Writes to the `companies` table (RLS:
 * only the super-admin can read/write all companies). Adding a company here is
 * all it takes — the server derives each company's token namespace from its
 * slug (jobber_<slug> / gmail_<slug> / qb_<slug>), so no code change is needed.
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiFetch";
import { usePermissions } from "../hooks/usePermissions";

// Feature key -> label. Order/labels mirror the app's tabs.
const FEATURES = [
  ["requests", "Requests"], ["quotes", "Quote Builder"], ["quote_templates", "Quote Templates"],
  ["pricelist", "Price List"], ["calculators", "Calculators"], ["crew", "Crew Performance"],
  ["jobs", "Jobs"], ["expenses", "Expenses"], ["reconciliation", "Reconciliation"],
  ["qb_audit", "QB Audit"], ["coach", "Coach"], ["marketing", "Marketing"], ["social", "Social Studio"],
  ["autofill", "Job Auto-Fill"], ["manage_crew", "Manage Crew"], ["feedback", "Feedback"],
  ["jaiman", "Jai-Man Learning"], ["admin", "Admin"],
];

const C = {
  bg: "#0d0f12", card: "#15181d", border: "#252a32", text: "#e8ebf0",
  muted: "#8892a4", accent: "#a5ce3a", danger: "#ef4444",
};

export default function CompanyDashboard() {
  const { isSuperadmin } = usePermissions() || {};
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ slug: "", name: "", apEmail: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState({}); // { slug: { qb, jobber, gmail } }

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from("companies").select("*").order("display_name");
    if (error) setErr(error.message);
    setCompanies(data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Connection status (QB / Jobber / Gmail) — super-admin-only server endpoint.
  const loadStatus = useCallback(async () => {
    try {
      const r = await apiFetch("/api/company-status");
      if (r.ok) { const j = await r.json(); setStatus(j.status || {}); }
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus, companies.length]);

  const deleteCompany = async (company) => {
    if (company.id === "browning-electric") { setErr("Browning can't be deleted."); return; }
    if (!window.confirm(`Delete "${company.display_name}"? This removes the company; their connected tokens stay until you disconnect them.`)) return;
    setCompanies((cs) => cs.filter((c) => c.id !== company.id)); // optimistic
    const { error } = await supabase.from("companies").delete().eq("id", company.id);
    if (error) { setErr(error.message); load(); }
  };

  const addCompany = async () => {
    const slug = form.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/^-+|-+$/g, "");
    if (!slug || !form.name.trim()) { setErr("Slug and company name are required."); return; }
    setSaving(true); setErr("");
    const features = {};
    FEATURES.forEach(([k]) => { features[k] = (k === "expenses" || k === "pricelist"); });
    const { error } = await supabase.from("companies").insert({
      id: slug,
      display_name: form.name.trim(),
      ap_email: form.apEmail.trim() || null,
      jobber_token_id: `jobber_${slug}`,
      gmail_token_id: `gmail_${slug}`,
      qb_token_id: `qb_${slug}`,
      features,
    });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setForm({ slug: "", name: "", apEmail: "" });
    load();
  };

  const toggleFeature = async (company, key) => {
    const features = { ...(company.features || {}), [key]: !(company.features?.[key]) };
    setCompanies((cs) => cs.map((c) => (c.id === company.id ? { ...c, features } : c)));
    const { error } = await supabase.from("companies").update({ features }).eq("id", company.id);
    if (error) { setErr(error.message); load(); }
  };

  if (isSuperadmin === false) {
    return <div style={{ minHeight: "100vh", background: C.bg, color: C.muted, padding: 48 }}>Super-admin only.</div>;
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, padding: "28px 24px", fontFamily: "'Roboto', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent, fontSize: 40, letterSpacing: "0.04em", margin: "0 0 4px" }}>Companies</h1>
        <div style={{ color: C.muted, fontSize: 14, marginBottom: 24 }}>
          Add companies and choose which features each one sees. Browning has everything on.
        </div>

        {err && (
          <div style={{ background: "rgba(239,68,68,0.1)", border: `1px solid ${C.danger}`, color: "#ffb4b4", padding: "10px 14px", borderRadius: 8, marginBottom: 18, fontSize: 13 }}>
            {err}
          </div>
        )}

        {/* Add company */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 28 }}>
          <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Add a company</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="Company name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="Acme Electric" />
            <Field label="Slug (id)" value={form.slug} onChange={(v) => setForm((f) => ({ ...f, slug: v }))} placeholder="acme-electric" />
            <Field label="AP email (invoices)" value={form.apEmail} onChange={(v) => setForm((f) => ({ ...f, apEmail: v }))} placeholder="ap@acmeelectric.com" wide />
            <button onClick={addCompany} disabled={saving}
              style={{ padding: "10px 22px", borderRadius: 8, background: C.accent, color: C.bg, fontWeight: 700, border: "none", cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1, fontSize: 14, height: 40 }}>
              {saving ? "Adding…" : "Add"}
            </button>
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 10 }}>
            New companies start with Expenses + Price List on. After adding, send them the connect links:
            <code style={{ color: C.accent }}> /api/quickbooks-auth?action=connect&company=SLUG</code>,
            <code style={{ color: C.accent }}> /api/jobber-auth?action=connect&company=SLUG</code>,
            <code style={{ color: C.accent }}> /api/gmail-auth?action=connect&company=SLUG</code>.
          </div>
        </div>

        {/* Company list */}
        {loading ? (
          <div style={{ color: C.muted }}>Loading…</div>
        ) : (
          companies.map((company) => (
            <div key={company.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 17 }}>{company.display_name}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ color: C.muted, fontSize: 12, fontFamily: "monospace" }}>{company.id}</div>
                  {company.id !== "browning-electric" && (
                    <button onClick={() => deleteCompany(company)} title="Delete company"
                      style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.danger, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, marginBottom: 14, fontSize: 12 }}>
                <span style={{ color: C.muted }}>{company.ap_email || "no AP email set"}</span>
                <ConnBadge label="QuickBooks" on={status[company.id]?.qb} />
                <ConnBadge label="Jobber" on={status[company.id]?.jobber} />
                <ConnBadge label="Gmail" on={status[company.id]?.gmail} />
                {company.id !== "browning-electric" && <EditCompany company={company} onSaved={load} />}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
                {FEATURES.map(([key, label]) => {
                  const on = company.features?.[key] === true;
                  return (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", color: on ? C.text : C.muted, userSelect: "none" }}>
                      <input type="checkbox" checked={on} onChange={() => toggleFeature(company, key)} style={{ accentColor: C.accent, width: 16, height: 16 }} />
                      {label}
                    </label>
                  );
                })}
              </div>
              <InviteRow company={company} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function EditCompany({ company, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(company.display_name || "");
  const [apEmail, setApEmail] = useState(company.ap_email || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const save = async () => {
    setBusy(true); setMsg("");
    const { error } = await supabase
      .from("companies")
      .update({ display_name: name.trim() || company.id, ap_email: apEmail.trim() || null })
      .eq("id", company.id);
    setBusy(false);
    if (error) setMsg(error.message);
    else { setOpen(false); onSaved && onSaved(); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{ background: "transparent", border: "none", color: C.accent, cursor: "pointer", fontSize: 12, padding: 0, textDecoration: "underline" }}>
        Edit details
      </button>
    );
  }
  const inp = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 9px", color: C.text, fontSize: 13, outline: "none" };
  return (
    <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Company name" style={{ ...inp, minWidth: 150 }} />
      <input value={apEmail} onChange={(e) => setApEmail(e.target.value)} placeholder="AP email" style={{ ...inp, minWidth: 180 }} />
      <button onClick={save} disabled={busy} style={{ padding: "6px 14px", borderRadius: 6, background: C.accent, color: C.bg, fontWeight: 700, border: "none", cursor: "pointer", fontSize: 12 }}>{busy ? "…" : "Save"}</button>
      <button onClick={() => { setOpen(false); setName(company.display_name || ""); setApEmail(company.ap_email || ""); }} style={{ padding: "6px 12px", borderRadius: 6, background: "transparent", color: C.muted, border: `1px solid ${C.border}`, cursor: "pointer", fontSize: 12 }}>Cancel</button>
      {msg && <span style={{ color: C.danger, fontSize: 12 }}>{msg}</span>}
    </span>
  );
}

function InviteRow({ company }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("admin");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const invite = async () => {
    if (!email.trim()) { setMsg("Enter an email."); return; }
    setBusy(true); setMsg("");
    try {
      const r = await apiFetch("/api/company-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), displayName: name.trim(), role, company: company.id }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) setMsg(d.error || "Failed to invite.");
      else if (d.emailSent) { setMsg(`Invited ${email} — email sent.`); setEmail(""); setName(""); }
      else if (d.inviteLink) setMsg(`User created. Email didn't send — share this link: ${d.inviteLink}`);
      else setMsg(d.warning || "User created.");
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };

  const inp = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 13, outline: "none" };
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
      <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Invite a user to this company</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="their@email.com" style={{ ...inp, minWidth: 200 }} />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" style={{ ...inp, minWidth: 150 }} />
        <select value={role} onChange={(e) => setRole(e.target.value)} style={{ ...inp }}>
          <option value="admin">Admin</option>
          <option value="owner">Owner</option>
          <option value="crew">Crew</option>
        </select>
        <button onClick={invite} disabled={busy}
          style={{ padding: "8px 18px", borderRadius: 6, background: C.accent, color: C.bg, fontWeight: 700, border: "none", cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, fontSize: 13 }}>
          {busy ? "Inviting…" : "Invite"}
        </button>
      </div>
      {msg && <div style={{ color: msg.startsWith("Invited") ? C.accent : C.muted, fontSize: 12, marginTop: 8, wordBreak: "break-all" }}>{msg}</div>}
    </div>
  );
}

function ConnBadge({ label, on }) {
  const color = on === undefined ? C.muted : on ? C.accent : C.danger;
  const dot = on === undefined ? "•" : on ? "✓" : "✗";
  return (
    <span style={{ color, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ fontWeight: 700 }}>{dot}</span>{label}
    </span>
  );
}

function Field({ label, value, onChange, placeholder, wide }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: wide ? 240 : 170 }}>
      <span style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 12px", color: C.text, fontSize: 14, outline: "none" }} />
    </div>
  );
}
